import { useCallback } from 'react';
import { gql, useQuery, ApolloQueryResult } from '@apollo/client';
import { last } from 'lodash';
import {
  ContactTag,
  Query,
  QueryContactTagsArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

export const CONTACT_TAGS = gql`
  query GetContactsTags(
    $institutionId: Int!
    $where: ContactTagWhereInput
    $after: String
    $first: Int
  ) {
    contactTags(
      institutionId: $institutionId
      orderBy: { name: ASC }
      where: $where
      after: $after
      first: $first
    ) {
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        nextCursor
        previousCursor
        startCursor
      }
      edges {
        cursor
        node {
          id
          name
          isActive
        }
      }
    }
  }
`;

export type ContactTagRefetchType = (
  variables?: Partial<Partial<QueryContactTagsArgs>> | undefined,
) => Promise<ApolloQueryResult<Pick<Query, 'contactTags'>>>;

export type UseContactTagsReturn = {
  loading: boolean;
  tags: ContactTag[];
  refetch: ContactTagRefetchType;
  loadMore: (variables?: Partial<Partial<QueryContactTagsArgs>>) => Promise<void>;
  hasNextPage?: boolean;
};

export const useContactTags = (): UseContactTagsReturn => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { data, loading, refetch, fetchMore } = useQuery<
    Pick<Query, 'contactTags'>,
    Partial<QueryContactTagsArgs>
  >(CONTACT_TAGS, {
    variables: {
      institutionId: currentInstitution.id,
      first: 100,
      where: {
        isActive: { _eq: true },
      },
    },
    onError: handleGraphQLError,
  });

  const loadMore = useCallback(async () => {
    const lastCursor = last(data?.contactTags.edges)?.cursor;

    if (!lastCursor) {
      return;
    }

    await fetchMore({
      variables: {
        after: lastCursor,
        first: 100,
        where: { isActive: { _eq: true } },
      },
    });
  }, [data?.contactTags.edges, fetchMore]);

  return {
    tags: getNodesFromConnection(data?.contactTags),
    loading,
    refetch,
    loadMore,
    hasNextPage: data?.contactTags?.pageInfo.hasNextPage,
  };
};
